/**
 * <ec-home-tranches>
 *
 * A tablist of expanding category slices: exactly one is always selected, so
 * tab semantics fit better than a disclosure/accordion. Mirrors the keyboard
 * model of ec-product-tabs.js — arrow keys with automatic activation and a
 * roving tabindex — with an orientation that follows the responsive layout
 * (horizontal slices on desktop, a vertical stack on mobile).
 *
 * Desktop widths are driven here in pixels (flex-basis): a CSS transition on
 * flex-basis is unreliable in Chromium, so the open/close motion is tweened
 * with the timing loop below. Mobile (column) layout is height-based and
 * handled entirely in CSS.
 */

const MOBILE_QUERY = '(max-width: 640px)';
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
const ACTIVE_RATIO = 4.2;
const DURATION = 700;
/** Fallback when --ec-tranches-fade-max cannot be read (kept in sync with CSS). */
const FADE_MAX = 44;
/** Below this, the leftover is a rounding artefact, not content worth signalling. */
const FADE_EPSILON = 1;

class EcHomeTranches extends HTMLElement {
  #slices = [];
  #tabs = [];
  #panels = [];
  #scrollers = [];
  #activeIndex = 0;
  #raf = 0;
  #resizeRaf = 0;
  #fadeMax = FADE_MAX;
  #mobileMql = null;
  #reducedMql = null;
  #resizeObserver = null;
  #onClick = null;
  #onKeydown = null;
  #onResize = null;
  #onMobileChange = null;
  #onScroll = null;

  connectedCallback() {
    this.#slices = Array.from(this.querySelectorAll(':scope > .ec-home-tranches__slice'));
    if (this.#slices.length === 0) return;
    this.#tabs = this.#slices.map((s) => s.querySelector('[role="tab"]'));
    this.#panels = this.#slices.map((s) => s.querySelector('[role="tabpanel"]'));

    this.#activeIndex = Math.max(
      0,
      this.#slices.findIndex((s) => s.classList.contains('is-active'))
    );

    this.#mobileMql = window.matchMedia(MOBILE_QUERY);
    this.#reducedMql = window.matchMedia(REDUCED_MOTION_QUERY);

    this.#onClick = (event) => {
      const tab = event.target.closest?.('[role="tab"]');
      if (!tab || !this.contains(tab)) return;
      const index = this.#tabs.indexOf(tab);
      if (index >= 0 && index !== this.#activeIndex) this.#activate(index);
    };

    // WAI-ARIA tablist keys with automatic activation, like ec-product-tabs.js.
    // Both axes are accepted so it works in either orientation.
    this.#onKeydown = (event) => {
      const tab = event.target.closest?.('[role="tab"]');
      if (!tab || !this.contains(tab)) return;
      const last = this.#tabs.length - 1;
      let next = this.#activeIndex;
      switch (event.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          next = this.#activeIndex >= last ? 0 : this.#activeIndex + 1;
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          next = this.#activeIndex <= 0 ? last : this.#activeIndex - 1;
          break;
        case 'Home':
          next = 0;
          break;
        case 'End':
          next = last;
          break;
        default:
          return;
      }
      event.preventDefault();
      this.#activate(next);
      this.#tabs[next]?.focus();
    };

    this.#onResize = () => {
      if (this.#resizeRaf) cancelAnimationFrame(this.#resizeRaf);
      this.#resizeRaf = requestAnimationFrame(() => this.#layout());
    };
    this.#onMobileChange = () => {
      this.#syncOrientation();
      this.#layout();
    };

    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeydown);
    window.addEventListener('resize', this.#onResize);
    this.#mobileMql.addEventListener('change', this.#onMobileChange);

    this.#setupScrollFades();

    this.classList.add('is-ready');
    this.#syncOrientation();
    this.#layout();
  }

  disconnectedCallback() {
    if (this.#raf) cancelAnimationFrame(this.#raf);
    if (this.#resizeRaf) cancelAnimationFrame(this.#resizeRaf);
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('keydown', this.#onKeydown);
    window.removeEventListener('resize', this.#onResize);
    this.#mobileMql?.removeEventListener('change', this.#onMobileChange);
    this.#resizeObserver?.disconnect();
    this.#scrollers.forEach((el) => el.removeEventListener('scroll', this.#onScroll));
  }

  /**
   * Scroll affordance. The panels keep a fixed height, so a long tranche cuts
   * its list mid-air with nothing but a thin scrollbar to say so. Each scroller
   * therefore carries a mask whose fade at either edge is as long as the content
   * still hidden there, capped at --ec-tranches-fade-max: it tapers to nothing
   * on arrival at an edge, and stays at nothing for a list that fits — which is
   * measured, never derived from an entry count, since the menu is the
   * merchant's to change.
   */
  #setupScrollFades() {
    this.#scrollers = this.#slices
      .map((slice) => slice.querySelector('.ec-home-tranches__panel-body'))
      .filter(Boolean);
    if (this.#scrollers.length === 0) return;

    const declared = parseFloat(
      getComputedStyle(this.#scrollers[0]).getPropertyValue('--ec-tranches-fade-max')
    );
    if (declared > 0) this.#fadeMax = declared;

    this.#onScroll = (event) => this.#syncFade(event.currentTarget);
    this.#scrollers.forEach((el) =>
      el.addEventListener('scroll', this.#onScroll, { passive: true })
    );

    // Widths change all through the 700ms tween, which rewraps the list and so
    // changes what overflows; the entries' own height changes on font swap or a
    // menu edit in the theme editor. Observing both keeps the fade honest during
    // the opening animation instead of letting it snap once at the end.
    if ('ResizeObserver' in window) {
      this.#resizeObserver = new ResizeObserver((entries) => {
        entries.forEach((entry) => {
          const scroller = entry.target.closest('.ec-home-tranches__panel-body');
          if (scroller) this.#syncFade(scroller);
        });
      });
      this.#scrollers.forEach((el) => {
        this.#resizeObserver.observe(el);
        const list = el.querySelector('.ec-home-tranches__sub-list');
        if (list) this.#resizeObserver.observe(list);
      });
    }

    this.#scrollers.forEach((el) => this.#syncFade(el));
  }

  /**
   * Runs after layout (scroll / resize callbacks), so these reads are free.
   * Values land on the wrapper, not the scroller: the chevron is a sibling of
   * the scroller and has to inherit them too.
   */
  #syncFade(el) {
    const above = el.scrollTop;
    const below = el.scrollHeight - el.clientHeight - el.scrollTop;
    const holder = el.closest('.ec-home-tranches__scroll') || el;
    holder.style.setProperty('--ec-tranches-fade-top', this.#fadeLength(above));
    holder.style.setProperty('--ec-tranches-fade-bottom', this.#fadeLength(below));
    // Same ramp as the bottom fade, as a 0–1 opacity for the chevron.
    holder.style.setProperty('--ec-tranches-cue', this.#hidden(below) / this.#fadeMax);
  }

  #fadeLength(hidden) {
    return `${this.#hidden(hidden)}px`;
  }

  #hidden(distance) {
    return distance > FADE_EPSILON ? Math.min(distance, this.#fadeMax) : 0;
  }

  #syncOrientation() {
    this.setAttribute('aria-orientation', this.#isColumn() ? 'vertical' : 'horizontal');
  }

  #isColumn() {
    return getComputedStyle(this).flexDirection.startsWith('column');
  }

  /** Pixel sizes for the active slice and the others, gap-aware. */
  #sliceWidths() {
    const gap = parseFloat(getComputedStyle(this).gap) || 0;
    const usable = this.clientWidth - gap * (this.#slices.length - 1);
    const total = ACTIVE_RATIO + (this.#slices.length - 1);
    return { wActive: (usable * ACTIVE_RATIO) / total, wOther: usable / total };
  }

  #layout() {
    if (this.#slices.length === 0) return;

    // Mobile: clear desktop px widths, CSS owns the column layout.
    if (this.#isColumn()) {
      this.#slices.forEach((s) => {
        s.style.flexBasis = '';
      });
      return;
    }

    const { wActive, wOther } = this.#sliceWidths();
    this.#slices.forEach((s, i) => {
      s.style.flexBasis = `${i === this.#activeIndex ? wActive : wOther}px`;
    });
  }

  #activate(index) {
    this.#slices.forEach((slice, i) => {
      const active = i === index;
      slice.classList.toggle('is-active', active);

      const tab = this.#tabs[i];
      if (tab) {
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        tab.tabIndex = active ? 0 : -1;
      }

      const panel = this.#panels[i];
      if (panel) {
        if (active) panel.removeAttribute('inert');
        else panel.setAttribute('inert', '');
      }
    });

    this.#activeIndex = index;

    if (this.#isColumn() || this.#reducedMql?.matches) this.#layout();
    else this.#animate(index);
  }

  #animate(index) {
    const { wActive, wOther } = this.#sliceWidths();
    const from = this.#slices.map(
      (s) => parseFloat(s.style.flexBasis) || s.getBoundingClientRect().width
    );
    const to = this.#slices.map((_, i) => (i === index ? wActive : wOther));
    const start = performance.now();
    const ease = (t) => 1 - Math.pow(1 - t, 3);

    if (this.#raf) cancelAnimationFrame(this.#raf);
    const tick = (now) => {
      const t = Math.min(1, (now - start) / DURATION);
      const e = ease(t);
      this.#slices.forEach((s, i) => {
        s.style.flexBasis = `${from[i] + (to[i] - from[i]) * e}px`;
      });
      this.#raf = t < 1 ? requestAnimationFrame(tick) : 0;
    };
    this.#raf = requestAnimationFrame(tick);
  }
}

if (!customElements.get('ec-home-tranches')) {
  customElements.define('ec-home-tranches', EcHomeTranches);
}
